import { Edge, Node } from '@xyflow/react';
import {
  ApprovalBranchesUpdateTypeInput,
  ApprovalBranchInput,
  ApprovalConditionsUpdateTypeInput,
  ApprovalCreateInput,
  ApprovalSelectionConditionInput,
  ApprovalTreeBranchesTypeInput,
  ApprovalTreeInput,
  ApprovalTreesUpdateTypeInput,
  ApprovalUpdateInput,
  Colors,
  EntityDeleteInput,
  InputMaybe,
} from 'common/types/globalTypes';
import { isEqual } from 'lodash';
import {
  Approval,
  Approval_approval_approvalSelectionConditions_nodes,
  Approval_Query,
} from 'settings/account/approvalSetup/view/__generated__/Approval';
import { ApprovalsInfo } from '../../../__generated__/ApprovalsInfo';
import {
  APPROVAL_BRANCH_INITIAL_VALUES,
  APPROVAL_LEVELS_INITIAL_VALUES,
  APPROVAL_SETUP_INITIAL_VALUES,
  REQUESTOR_INITIAL_VALUES,
} from '../../constants';
import {
  ApprovalBranchViewRowValues,
  ApprovalLevelViewRowValues,
  ApprovalValues,
  BranchData,
} from '../../types';
import { FlowApproval } from 'settings/account/intuitiveApprovalSetupFlow/__generated__/FlowApproval';

function omitKeys<T extends object, K extends keyof T>(
  obj: T,
  keys: K[]
): Omit<T, K> {
  const result = { ...obj };
  for (const key of keys) {
    delete result[key];
  }
  return result;
}

export const getDefaultValues = (
  approvalDetailsData: FlowApproval | undefined,
  isNew: boolean
): ApprovalValues => {
  let initialValues: ApprovalValues = APPROVAL_SETUP_INITIAL_VALUES;
  const { approval } = { ...approvalDetailsData };
  const { nodes } = { ...approval?.approvalSelectionConditions };
  if (approval && !isNew) {
    if (approval?._isUpdatable) {
      initialValues = {
        ...approval!,
        badgeColor: approval.badgeColor,
        // badgeColor: approval.bad
        approvalSelectionConditions:
          nodes?.length! > 0
            ? [...nodes!, ...[REQUESTOR_INITIAL_VALUES]]
            : [REQUESTOR_INITIAL_VALUES],
        approvalTrees:
          approval.approvalTrees.nodes.length > 0
            ? [
                ...approval.approvalTrees.nodes.map((tree) => ({
                  ...tree,
                  requiredBranchApprovalCount:
                    tree.requiredBranchApprovalCount.toString(),
                  approvalBranches: [
                    ...tree.approvalBranches.nodes,
                    APPROVAL_BRANCH_INITIAL_VALUES,
                  ],
                })),
                { ...APPROVAL_LEVELS_INITIAL_VALUES },
              ]
            : [APPROVAL_LEVELS_INITIAL_VALUES],
      };
    } else {
      initialValues = {
        ...approval!,
        approvalSelectionConditions: nodes!,
        approvalTrees: approval.approvalTrees.nodes.map((tree) => ({
          ...tree,
          requiredBranchApprovalCount:
            tree.requiredBranchApprovalCount.toString(),
          approvalBranches: tree.approvalBranches.nodes,
        })),
      };
    }
  }
  return initialValues;
};
const constructBranchData = (branchData: ApprovalBranchViewRowValues) => {
  return {
    description: branchData.description!,
    branchOrder: Number(branchData.branchOrder?.toString()),
    budgetLimitAmount: branchData.budgetLimitAmount,
    budgetLimitPercent: branchData.budgetLimitPercent,
    isOverBudgetSelector: branchData.isOverBudgetSelector,
    isBusinessUnitSelector: branchData.isBusinessUnitSelector,
    isDepartmentSelector: branchData.isDepartmentSelector,
    isProjectSelector: branchData.isProjectSelector,
    isUserGroupSelector: branchData.isUserGroupSelector,
    isWorkgroupSelector: branchData.isWorkgroupSelector,
    userOccupationTitleIdSelector: branchData.userOccupation?.id,
    userRankTypeIdSelector: branchData.userRank?.id,
    transactionStartAmountTotal: branchData.transactionStartAmountTotal,
    transactionEndAmountTotal: branchData.transactionEndAmountTotal,
    transactionAmountConversionTypeId:
      branchData.transactionAmountConversionTypeId,
    approverTransactionTypeId: branchData.approverTransactionTypeId,
    transactionBusinessUnitId: branchData.transactionBusinessUnitId,
    transactionDepartmentId: branchData.transactionDepartmentId,
    transactionDepartmentGroupId: branchData.transactionDepartmentGroupId,
    requesterUserId: branchData.requesterUserId,
    isTransactionNotCondition: branchData.isTransactionNotCondition,
    approverDelayDays: Number(branchData.approverDelayDays),
    approverDelayHours: Number(branchData.approverDelayHours),
    approverDelayStatusTypeId: branchData.approverDelayStatusTypeId,
  };
};

export const getUpdateApprovalFlowValues = (
  values: ApprovalValues,
  approvalDetailsData: Approval_Query | undefined,
  _: ApprovalsInfo | undefined
): ApprovalUpdateInput => {
  const valuesToOmit: string[] = [
    'id',
    'approvalId',
    '_isUpdatable',
    '_isDeletable',
    '_rowTimestamp',
    'approval',
    'approvalBranches',
    '__typename',
    '_infoLines',
    'levelName',
    'levelIndex',
    'disabled',
    'updatable',
    'isFlowDeletable',
    'isFlowUpdatable',
  ];
  const approvalConditionsCreate: ApprovalSelectionConditionInput[] =
    values.approvalSelectionConditions?.reduce((res, item) => {
      if (item.id === null) {
        // @ts-ignore
        const arrItem = omitKeys(item, valuesToOmit);

        return [
          {
            ...arrItem,
            importanceLevel: Number(item.importanceLevel?.toString()),
            startElapsedDays: Number(item.startElapsedDays?.toString()),
            endElapsedDays: Number(item.endElapsedDays?.toString()),
            description: item.description!,
          },
          ...res,
        ];
      } else return res;
    }, [] as ApprovalSelectionConditionInput[]) || [];

  // @ts-ignore
  const approvalConditionsUpdate: ApprovalConditionsUpdateTypeInput[] =
    values.approvalSelectionConditions
      ?.filter((item) => item.id !== null)
      .reduce((res, item) => {
        const oldConditions = [
          ...(approvalDetailsData?.approval?.approvalSelectionConditions
            .nodes || []),
        ];
        const oldConditionNode = oldConditions.find((fd) => fd.id === item.id);

        if (oldConditionNode) {
          const patch: any = Object.entries(item).reduce((res, [key, val]) => {
            if (
              key === 'approval' ||
              key === '_infoLines' ||
              key === 'isFlowUpdatable' ||
              key === 'disabled'
            )
              return res;
            if (
              val !==
              oldConditionNode[
                key as keyof Approval_approval_approvalSelectionConditions_nodes
              ]
            ) {
              if (
                key === 'importanceLevel' ||
                key === 'startElapsedDays' ||
                key === 'endElapsedDays'
              )
                return { ...res, [key]: Number(val) };

              return { ...res, [key]: val };
            }
            return res;
          }, {});
          if (Object.entries(patch).length)
            return [
              {
                id: item.id,
                rowTimestamp: item._rowTimestamp,
                approvalConditionPatch: patch,
              },
              ...res,
            ] as ApprovalConditionsUpdateTypeInput[];
          else return res;
        }
        return [...res];
      }, [] as ApprovalConditionsUpdateTypeInput[]);

  const approvalConditionsDelete: EntityDeleteInput[] =
    approvalDetailsData?.approval?.approvalSelectionConditions?.nodes
      ?.filter((existing) => {
        if (!existing.id) return false;
        return !values.approvalSelectionConditions?.some(
          (current) => current.id === existing.id
        );
      })
      .map(
        (item) =>
          ({
            id: item.id,
            rowTimestamp: item._rowTimestamp,
          } as EntityDeleteInput)
      ) || [];

  let updateApprovalBranches: ApprovalBranchesUpdateTypeInput[] = []; // only to update branch
  let treeBranches: ApprovalTreeBranchesTypeInput[] = []; // for new tree and branch
  let treeUpdate: ApprovalTreesUpdateTypeInput[] = []; // only to update tree

  //update treeUpdate

  values.approvalTrees?.forEach((item, index) => {
    if (
      values?.approvalTrees?.length &&
      index === values?.approvalTrees?.length - 1
    )
      return;
    // @ts-ignore
    const arr = omitKeys(item, [...valuesToOmit]);
    const existingItem =
      approvalDetailsData?.approval?.approvalTrees?.nodes?.find(
        (node) => node.id === item.id
      );

    if (!existingItem || item.id === null) return;
    // @ts-ignore

    const existingArr = omitKeys(existingItem, [...valuesToOmit]);

    const currentPatch = {
      ...arr,
      approvalLevel: Number(item.approvalLevel),
      requiredBranchApprovalCount: Number(
        item.requiredBranchApprovalCount!.toString()
      ),
    };

    const existingPatch = {
      ...existingArr,
      approvalLevel: Number(existingItem.approvalLevel),
      requiredBranchApprovalCount: Number(
        existingItem.requiredBranchApprovalCount!.toString()
      ),
    };

    if (!isEqual(currentPatch, existingPatch)) {
      const tempObj: ApprovalTreesUpdateTypeInput = {
        id: item.id,
        rowTimestamp: item._rowTimestamp,
        approvalTreePatch: currentPatch,
      };
      treeUpdate.push(tempObj);
    }
  });
  //update treeBranches

  values.approvalTrees?.map((item, index) => {
    if (index !== values.approvalTrees?.length! - 1) {
      if (item.id === null) {
        const branches =
          item.approvalBranches &&
          item.approvalBranches
            ?.filter((data) => data.description)
            ?.map((data) => {
              return {
                ...constructBranchData(data),
              } as ApprovalBranchInput;
            });
        const tempObj: ApprovalTreeBranchesTypeInput = {
          approvalTree: {
            // @ts-ignore
            ...(omitKeys(item, valuesToOmit) as ApprovalTreeInput),
            approvalLevel: Number(item.approvalLevel?.toString()),
            requiredBranchApprovalCount: Number(
              item.requiredBranchApprovalCount?.toString()
            ),
          },
          approvalBranches: branches,
        };
        treeBranches.push(tempObj);
      }
    }
    return true;
  });

  //update toBeDeletedRequesterTree

  const currentIds = new Set(
    values?.approvalTrees
      ?.filter((tree: any) => tree?.id != null)
      .map((tree: any) => tree.id)
  );

  const toBeDeletedRequesterTree: EntityDeleteInput[] = [];
  if (approvalDetailsData?.approval?.approvalTrees)
    for (const item of approvalDetailsData?.approval?.approvalTrees?.nodes) {
      if (item?.id && !currentIds.has(item.id)) {
        toBeDeletedRequesterTree.push({
          id: item.id,
          rowTimestamp: item._rowTimestamp!,
        });
      }
    }

  const hasChanges = (
    newBranch: ApprovalBranchViewRowValues,
    oldBranch: ApprovalBranchViewRowValues
  ) => {
    if (!oldBranch) return true;
    const newData = constructBranchData(newBranch);
    const oldData = constructBranchData(oldBranch);

    return (Object.keys(newData) as (keyof BranchData)[]).some((key) => {
      if (newData[key] == null && oldData[key] == null) return false;
      if (newData[key] == null || oldData[key] == null) return true;
      if (
        typeof newData[key] === 'object' &&
        typeof oldData[key] === 'object'
      ) {
        return JSON.stringify(newData[key]) !== JSON.stringify(oldData[key]);
      }
      return newData[key] !== oldData[key];
    });
  };
  values.approvalTrees?.map((item, index) => {
    if (index !== values.approvalTrees?.length! - 1) {
      item.approvalBranches
        ?.filter((branch) => branch.description)
        ?.map((branches) => {
          const existingBranch =
            approvalDetailsData?.approval?.approvalTrees?.nodes[
              index
            ]?.approvalBranches?.nodes?.find((b) => b.id === branches.id);
          if (item.id) {
            if (
              branches.id !== null &&
              existingBranch &&
              hasChanges(branches, existingBranch)
            ) {
              const updateBranch: ApprovalBranchesUpdateTypeInput = {
                id: branches.id,
                rowTimestamp: branches._rowTimestamp,
                approvalBranchPatch: {
                  ...constructBranchData(branches),
                },
              };
              updateApprovalBranches.push(updateBranch);
            } else if (branches.id === null) {
              const tempObj: ApprovalTreeBranchesTypeInput = {
                approvalTreeId: item.id,
                approvalBranches: [
                  {
                    ...constructBranchData(branches),
                  },
                ],
              };
              treeBranches.push(tempObj);
            }
          }
          return true;
        });
    }
    return true;
  });

  const toBeDeletedApprovalBranch: EntityDeleteInput[] = [];

  const originalTrees =
    approvalDetailsData?.approval?.approvalTrees?.nodes ?? [];
  const currentTrees = values?.approvalTrees ?? [];

  for (const originalTree of originalTrees) {
    const matchingTree = currentTrees.find((t) => t.id === originalTree.id);
    const currentBranchIds = new Set(
      matchingTree?.approvalBranches?.map((b) => b?.id).filter(Boolean)
    );

    for (const branch of originalTree.approvalBranches?.nodes ?? []) {
      if (branch?.id && !currentBranchIds.has(branch.id)) {
        toBeDeletedApprovalBranch.push({
          id: branch.id,
          rowTimestamp: branch._rowTimestamp!,
        });
      }
    }
  }

  const dataValues: ApprovalUpdateInput = {
    id: approvalDetailsData?.approval?.id!,
    rowTimestamp: approvalDetailsData?.approval?._rowTimestamp!,
    approvalBranchesDelete: toBeDeletedApprovalBranch,
    approvalBranchesUpdate: updateApprovalBranches,
    approvalPatch: {
      approvalTypeId: Number(values.approvalTypeId?.toString()),
      description: values.description,
      isAddApproversEligible: values.isAddApproversEligible,
      isPrimary: values.isPrimary,
      isNewApprovalSelectable: values.isNewApprovalSelectable,
      isDelayedApproverAllowed: values.isDelayedApproverAllowed,
      name: values.name,
      badgeColor: values.badgeColor as InputMaybe<Colors>,
      priorityNumber: Number(values.priorityNumber!.toString()),
      approvalPhaseTypeId: values.approvalPhaseTypeId!,
    },
    approvalConditionsCreate,
    approvalConditionsDelete,
    approvalConditionsUpdate,
    approvalTreeBranches: treeBranches,
    approvalTreesDelete: toBeDeletedRequesterTree,
    approvalTreesUpdate: treeUpdate,
  };

  return dataValues;
};

export const getCreateApprovalFlowValues = (
  values: ApprovalValues
): ApprovalCreateInput => {
  const newPriorityNumber = -999;
  const valuesToOmit = new Set([
    'id',
    'approvalId',
    '_isUpdatable',
    '_isDeletable',
    '_rowTimestamp',
    'approval',
    'approvalBranches',
    '__typename',
    'levelName',
    'levelIndex',
    'disabled',
    'isFlowUpdatable',
  ]);

  const approvalConditions: (ApprovalSelectionConditionInput | null)[] =
    values?.approvalSelectionConditions
      ? values?.approvalSelectionConditions
          .filter((data) => data.description && data.importanceLevel)
          .map((item) => {
            const cleanedItem = Object.entries(item).reduce(
              (acc, [key, value]) => {
                if (valuesToOmit.has(key)) return acc;

                // Ensure numeric conversion for specific fields
                if (
                  key === 'importanceLevel' ||
                  key === 'startElapsedDays' ||
                  key === 'endElapsedDays' ||
                  key === 'startAmountTotal' ||
                  key === 'endAmountTotal'
                ) {
                  return {
                    ...acc,
                    [key]: value !== null ? Number(value?.toString()) : null,
                  };
                }

                return { ...acc, [key]: value };
              },
              {} as ApprovalSelectionConditionInput
            );

            return cleanedItem;
          })
      : [];

  function mapApprovalLevel(
    level: ApprovalLevelViewRowValues
  ): ApprovalTreeInput {
    return {
      name: level.name!,
      description: level.description ?? null,
      approvalLevel: level.approvalLevel!,
      requiredBranchApprovalCount: Number(level.requiredBranchApprovalCount),
      isApprovedByConsensus: level.isApprovedByConsensus ?? null,
      isRejectOnUnderApprovalCount: level.isRejectOnUnderApprovalCount ?? null,
      isSingleDeclineConsensusInvoked:
        level.isSingleDeclineConsensusInvoked ?? null,
      isUsedForAdditionalDepartments:
        level.isUsedForAdditionalDepartments ?? null,
    };
  }

  function mapApprovalBranch(
    branch: ApprovalBranchViewRowValues
  ): ApprovalBranchInput {
    const numberConvertKeys: (keyof ApprovalBranchInput)[] = [
      'branchOrder',
      'approverDelayDays',
      'approverDelayHours',
      'transactionAmountConversionTypeId',
      'approverDelayStatusTypeId',
      'approverTransactionTypeId',
    ];

    const skipSourceKeys = new Set([
      'id',
      '_rowTimestamp',
      '_isUpdatable',
      '_isDeletable',
      'userOccupation',
      'userRank',
      'description',
      'levelName',
      'levelIndex',
      'isFlowUpdatable',
      'isFlowDeletable',
      'approverBranchIndex',
    ]);

    const result = (
      Object.keys(branch) as (keyof ApprovalBranchViewRowValues)[]
    ).reduce((acc, key) => {
      if (skipSourceKeys.has(key)) {
        return acc;
      }

      const sourceValue = branch[key];
      if (sourceValue != null) {
        if (numberConvertKeys.includes(key as keyof ApprovalBranchInput)) {
          (acc as any)[key] = Number(sourceValue);
        } else {
          (acc as any)[key] = sourceValue;
        }
      }
      return acc;
    }, {} as ApprovalBranchInput);

    result.description = branch.description!;
    result.userOccupationTitleIdSelector = branch.userOccupation?.id ?? null;
    result.userRankTypeIdSelector = branch.userRank?.id ?? null;

    return result;
  }

  function transformFormToApprovalTreeBranchesTypeInput(
    level: ApprovalLevelViewRowValues
  ): ApprovalTreeBranchesTypeInput {
    const approvalTree = mapApprovalLevel(level);
    const approvalTreeId = level.id ?? null;

    const approvalBranches = (level.approvalBranches ?? [])
      .filter((b) => !!b.description)
      .map(mapApprovalBranch);

    return {
      approvalTree,
      approvalTreeId,
      approvalBranches,
    };
  }
  const dataValues: ApprovalCreateInput = {
    approval: {
      approvalTypeId: Number(values.approvalTypeId!),
      name: values.name!,
      priorityNumber: newPriorityNumber,
      description: values.description!,
      badgeColor: values.badgeColor as InputMaybe<Colors>,
      approvalPhaseTypeId: values.approvalPhaseTypeId!,
      isAddApproversEligible: values.isAddApproversEligible!,
      isPrimary: values.isPrimary,
      isNewApprovalSelectable: values.isNewApprovalSelectable,
      isDelayedApproverAllowed: values.isDelayedApproverAllowed,
    },

    approvalConditions: approvalConditions,

    approvalTreeBranches: (values.approvalTrees ?? [])
      .filter((level) => !!level.name) // skip empty
      .map(transformFormToApprovalTreeBranchesTypeInput),
  };

  return dataValues;
};

export interface ClosestEdgeResult {
  id: string;
  source: string;
  target: string;
  className: string;
  type: string;
}

export function getClosestEdgeUtil(
  internalNode: Node | undefined,
  existingNodes: Node[]
): ClosestEdgeResult | null {
  if (!internalNode || !internalNode.position) return null;

  const nodeType = internalNode.type;

  const closestNode = existingNodes.reduce<{
    distance: number;
    node: Node | null;
  }>(
    (res, n) => {
      if (
        n.id !== internalNode.id &&
        n.position?.x != null &&
        n.type === nodeType
      ) {
        const dx = n.position.x - internalNode.position.x;
        const dy = n.position.y - internalNode.position.y;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d < res.distance && d < 400) {
          return { distance: d, node: n };
        }
      }
      return res;
    },
    {
      distance: Number.MAX_VALUE,
      node: null,
    }
  );

  if (!closestNode.node) {
    return null;
  }

  const closeNodeIsSource =
    closestNode.node.position.y < internalNode.position.y;

  return {
    id: closeNodeIsSource
      ? `${closestNode.node.id}-${internalNode.id}`
      : `${internalNode.id}-${closestNode.node.id}`,
    source: closeNodeIsSource ? closestNode.node.id : internalNode.id,
    target: closeNodeIsSource ? internalNode.id : closestNode.node.id,
    className: '',
    type: 'close-edge',
  };
}
export function getUpdatedEdgesWithReparenting(
  edges: Edge[],
  newEdge: {
    id?: string;
    source: string;
    target: string;
    className?: string;
    type?: string;
    sourceHandle?: string;
    targetHandle?: string;
    animated?: boolean;
    label?: string;
  }
): Edge[] {
  const { source: newSource, target: newTarget } = newEdge;
  let updatedEdges: Edge[] = [...edges];

  const newTargetParentEdge = updatedEdges.find(
    (edge) => edge.target === newTarget
  );
  const newTargetOriginalParent = newTargetParentEdge?.source || null;

  const newSourceChildEdge = updatedEdges.find(
    (edge) => edge.source === newSource
  );
  const newSourceOriginalChild = newSourceChildEdge?.target || null;

  const newSourceParentEdge = updatedEdges.find(
    (edge) => edge.target === newSource
  );
  const newSourceOriginalParent = newSourceParentEdge?.source || null;

  updatedEdges = updatedEdges.filter((edge) => {
    const isNewTargetParentEdge =
      newTargetParentEdge && edge.id === newTargetParentEdge.id;
    const isNewSourceChildEdge =
      newSourceChildEdge && edge.id === newSourceChildEdge.id;
    const isNewSourceOldParentEdge =
      newSourceParentEdge && edge.id === newSourceParentEdge.id;

    return (
      !isNewTargetParentEdge &&
      !isNewSourceChildEdge &&
      !isNewSourceOldParentEdge
    );
  });

  // Add the new edge
  updatedEdges.push({
    id: newEdge.id || `${newSource}-${newTarget}`,
    source: newSource,
    target: newTarget,
    className: newEdge.className,
    type: newEdge.type,
    sourceHandle: newEdge.sourceHandle,
    targetHandle: newEdge.targetHandle,
    animated: newEdge.animated,
    label: newTargetParentEdge?.label,
  });

  // Add re-parented and re-childed edges
  if (newSourceOriginalParent && newSourceOriginalChild) {
    updatedEdges.push({
      id: `${newSourceOriginalParent}-${newSourceOriginalChild}`,
      source: newSourceOriginalParent,
      target: newSourceOriginalChild,
      type: newSourceChildEdge?.type,
      className: newSourceChildEdge?.className,
      animated: newSourceChildEdge?.animated,
      label: newSourceChildEdge?.label,
    });
  }

  if (newTargetOriginalParent && newSource) {
    updatedEdges.push({
      id: `${newTargetOriginalParent}-${newSource}`,
      source: newTargetOriginalParent,
      target: newSource,
      type: newTargetParentEdge?.type,
      className: newTargetParentEdge?.className,
      animated: newTargetParentEdge?.animated,
      label: newSourceParentEdge?.label,
    });
  }

  // Deduplicate edges by ID
  const uniqueEdges = Array.from(
    new Map(updatedEdges.map((edge) => [edge.id, edge])).values()
  );

  return uniqueEdges;
}
